<template>
  <b-row>
    <b-col cols="12">
      <div v-if="entityData.assinaturaDigital" class="d-flex flex-column align-items-center mb-1">
        <label class="font-weight-bold mb-1">Assinatura Atual:</label>
        <div class="bg-transparent border rounded p-1 mb-1 shadow-sm d-flex align-items-center justify-content-center" style="min-height: 120px; min-width: 300px;">
          <img 
            :src="entityData.assinaturaDigital" 
            alt="Assinatura" 
            style="max-width: 100%; max-height: 150px;"
          />
        </div>
        <b-button 
          v-if="verificarPermissao('ROLE_ADMIM_ATUALIZAR')"
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="outline-primary" 
          size="sm" 
          @click="openModal"
        >
          <feather-icon icon="EditIcon" class="mr-50" />
          Alterar Assinatura
        </b-button>
      </div>
      <div v-else class="text-center p-3 border rounded border-dashed bg-light">
        <feather-icon icon="Edit3Icon" size="32" class="text-muted mb-1" />
        <p class="text-muted font-small-3">Nenhuma assinatura digital capturada para este registro.</p>
        <b-button 
          v-if="verificarPermissao('ROLE_ADMIM_ATUALIZAR')"
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary" 
          @click="openModal"
        >
          <feather-icon icon="PlusIcon" class="mr-50" />
          Capturar Assinatura
        </b-button>
      </div>

      <!-- Modal de Captura -->
      <b-modal
        :id="`modal-capture-signature-${uniqueId}`"
        ref="modal-capture-signature"
        title="Mesa de Assinatura Digital"
        size="lg"
        hide-footer
        centered
        no-close-on-backdrop
        header-bg-variant="primary"
        header-class="text-white"
        title-class="text-white"
      >
        <div class="p-1">
          <div class="alert alert-info py-1 px-2 font-small-3 mb-2">
            <feather-icon icon="InfoIcon" class="mr-50" />
            Assine no quadro branco abaixo. Você pode usar uma <strong>mesa digitalizadora</strong>, 
            <strong>tablet</strong>, ou o <strong>mouse</strong>.
          </div>
          
          <div class="signature-canvas-container border rounded bg-transparent mb-2 d-flex justify-content-center shadow-inner" style="touch-action: none; cursor: crosshair;">
            <vue-drawing-canvas
              ref="signaturePad"
              v-model="signatureImage"
              @update:image="signatureImage = $event"
              :width="canvasWidth"
              :height="200"
              stroke-type="dash"
              line-cap="round"
              line-join="round"
              :line-width="3"
              color="#000000"
              background-color="transparent"
              save-as="png"
              :styles="{ border: '1px solid #ebe9f1' }"
            />
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <b-button 
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary" 
              @click="clear"
            >
              <feather-icon icon="Trash2Icon" class="mr-50" />
              Limpar
            </b-button>
            
            <div>
              <b-button 
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                variant="flat-secondary" 
                class="mr-1"
                @click="$bvModal.hide(`modal-capture-signature-${uniqueId}`)"
              >
                Cancelar
              </b-button>
              <b-button 
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary" 
                @click="saveSignature"
                :disabled="!signatureImage || signatureImage.length < 200"
              >
                <feather-icon icon="CheckIcon" class="mr-50" />
                Confirmar Assinatura
              </b-button>
            </div>
          </div>
        </div>
      </b-modal>
    </b-col>
  </b-row>
</template>

<script>
import { BRow, BCol, BButton, BModal, VBModal } from 'bootstrap-vue'
import VueDrawingCanvas from 'vue-drawing-canvas'
import Ripple from 'vue-ripple-directive'
import { verificarPermissao } from '@/auth/utils'
import mixinsGlobal from '@/mixins'

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BModal,
    VueDrawingCanvas,
  },
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  mixins: [mixinsGlobal],
  props: {
    storeName: {
      type: String,
      required: true
    },
    objectName: {
      type: String,
      required: true
    },
    actionName: {
      type: String,
      required: true
    },
    prepareActionName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      verificarPermissao,
      signatureImage: '',
      canvasWidth: 600,
      uniqueId: Math.floor(Math.random() * 1000)
    }
  },
  computed: {
    entityData() {
      return this.$store.state[this.storeName][this.objectName]
    }
  },
  mounted() {
    this.updateCanvasWidth()
    window.addEventListener('resize', this.updateCanvasWidth)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateCanvasWidth)
  },
  methods: {
    updateCanvasWidth() {
      const width = window.innerWidth
      if (width < 768) {
        this.canvasWidth = width - 80
      } else {
        this.canvasWidth = 600
      }
    },
    openModal() {
      this.signatureImage = null
      this.$bvModal.show(`modal-capture-signature-${this.uniqueId}`)
      setTimeout(() => {
        if (this.$refs.signaturePad) {
          this.$refs.signaturePad.reset()
        }
      }, 200)
    },
    clear() {
      this.$refs.signaturePad.reset()
      this.signatureImage = ''
    },
    saveSignature() {
      if (!this.signatureImage) return

      const storeData = this.entityData
      
      // Função auxiliar robusta para extrair apenas o ID numérico
      const extractId = (val) => {
        if (val === null || val === undefined) return null
        if (typeof val === 'object' && val.id !== undefined) return val.id
        if (typeof val === 'object') return null // Objeto sem ID
        return val // Já é um valor primitivo
      }

      // Constrói o payload campo a campo para total controle
      const payload = {
        id: storeData.id,
        nome: storeData.nome,
        matricula: storeData.matricula,
        email: storeData.email,
        assinaturaDigital: this.signatureImage,
        
        // Mapeamento explícito de IDs
        sexo: extractId(storeData.sexo),
        estadoCivil: extractId(storeData.estadoCivil),
        profissao: extractId(storeData.profissao),
        nacionalidade: extractId(storeData.nacionalidade),
        naturalidade: extractId(storeData.naturalidade),
        tipoRegistro: extractId(storeData.tipoRegistro),
        situacaoRegistro: extractId(storeData.situacaoRegistro),
        
        // Dados textuais e datas (Formatando LocalDate corretamente YYYY-MM-DD)
        nomeMae: storeData.nomeMae,
        nomePai: storeData.nomePai,
        dataNascimento: storeData.dataNascimento ? String(storeData.dataNascimento).split('T')[0] : null,
        dataAdesao: storeData.dataAdesao,
        observacao: storeData.observacao,
        nomeSocial: storeData.nomeSocial
      }

      console.log('Enviando Payload:', JSON.parse(JSON.stringify(payload)))

      this.$store.dispatch(`${this.storeName}/${this.actionName}`, payload)
        .then(response => {
          if (response.response) {
            this.MensagemError('Falha ao Salvar Assinatura', response.response.data.error || 'Erro interno no servidor')
            return
          }
          
          this.$store.dispatch(`${this.storeName}/${this.prepareActionName}`, response.data)
            .then(() => {
              this.showMessageSuccess('Assinatura', 'Assinatura salva com sucesso!')
              this.$emit('saved', response.data)
              this.$bvModal.hide(`modal-capture-signature-${this.uniqueId}`)
            })
        })
        .catch(error => {
          this.MensagemError('Erro', 'Não foi possível salvar a assinatura.')
          console.error(error)
        })
    },
    getIdFromObject(obj) {
      if (!obj) return null
      return (typeof obj === 'object' && obj.id) ? obj.id : obj
    }
  }
}
</script>

<style lang="scss" scoped>
.signature-canvas-container {
  background-image: 
    linear-gradient(45deg, #f8f8f8 25%, transparent 25%), 
    linear-gradient(-45deg, #f8f8f8 25%, transparent 25%), 
    linear-gradient(45deg, transparent 75%, #f8f8f8 75%), 
    linear-gradient(-45deg, transparent 75%, #f8f8f8 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
}

.border-dashed {
  border-style: dashed !important;
  border-width: 2px !important;
}

.shadow-inner {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
}

::v-deep .modal-title {
  color: white !important;
}
</style>
